import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import client from '@/api/client';
import type { PaymentSubmissionSummary } from '@/api/types';
import { resolveUploadUrl } from '@/lib/fileUrl';
import { Pagination } from '@/components/ui/Pagination';
import { usePagination, paginateArray } from '@/hooks/usePagination';
import { paymentMethodLabel } from '@/lib/labels';

function fmt(n: number) {
  return '₹' + n.toLocaleString('en-IN');
}

interface PendingVerificationsPanelProps {
  hostelId: string;
  accentColor?: 'teal' | 'violet';
}

export function PendingVerificationsPanel({ hostelId, accentColor = 'teal' }: PendingVerificationsPanelProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const { page, size, setPage } = usePagination();

  const { data: submissions = [], isLoading } = useQuery<PaymentSubmissionSummary[]>({
    queryKey: ['payment-submissions', hostelId || 'ALL', 'PENDING'],
    queryFn: () => {
      const p = new URLSearchParams({ status: 'PENDING' });
      if (hostelId) p.set('hostelId', hostelId);
      return client.get(`/api/rent/payment-submissions?${p}`).then(r => r as unknown as PaymentSubmissionSummary[]);
    },
  });

  const invalidateAll = () => {
    void qc.invalidateQueries({ queryKey: ['payment-submissions'] });
    void qc.invalidateQueries({ queryKey: ['rent'] });
    void qc.invalidateQueries({ queryKey: ['payment-history'] });
  };

  const approveMutation = useMutation({
    mutationFn: (id: string) => client.post(`/api/rent/payment-submissions/${id}/approve`),
    onSuccess: invalidateAll,
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      client.post(`/api/rent/payment-submissions/${id}/reject`, { reason }),
    onSuccess: () => { setRejectingId(null); setRejectReason(''); invalidateAll(); },
  });

  const btnColor = accentColor === 'violet' ? 'text-violet-600' : 'text-teal-600';

  if (isLoading) {
    return <p className="text-gray-400 py-10 text-center text-sm">{t('common.loading')}</p>;
  }

  return (
    <div>
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
          <tr>
            <th className="px-4 py-3 text-left">{t('owner.tenant', 'Tenant')}</th>
            <th className="px-4 py-3 text-left">{t('owner.month', 'Month')}</th>
            <th className="px-4 py-3 text-right">{t('common.amount')}</th>
            <th className="px-4 py-3 text-left">{t('rent.paymentMethod')}</th>
            <th className="px-4 py-3 text-left">{t('owner.ref', 'Ref')}</th>
            <th className="px-4 py-3 text-left">{t('owner.paidAt', 'Paid At')}</th>
            <th className="px-4 py-3 text-left">{t('rent.receipt', 'Receipt')}</th>
            <th className="px-4 py-3 text-right">{t('common.actions')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {submissions.length === 0 && (
            <tr>
              <td colSpan={8} className="px-4 py-10 text-center text-gray-400">
                {t('rent.noPendingVerifications', 'No pending verifications.')}
              </td>
            </tr>
          )}
          {paginateArray(submissions, page, size).map(s => (
            <tr key={s.id} className="hover:bg-gray-50">
              <td className="px-4 py-3">
                <p className="font-medium text-gray-900">{s.tenantName}</p>
                <p className="text-xs text-gray-400">{s.tenantPhone}</p>
              </td>
              <td className="px-4 py-3 font-medium">{s.billingMonth ?? '—'}</td>
              <td className="px-4 py-3 text-right font-semibold">{fmt(Number(s.claimedAmount))}</td>
              <td className="px-4 py-3">
                <span className="bg-gray-100 text-gray-700 text-xs px-2 py-0.5 rounded-full">
                  {paymentMethodLabel(t, s.claimedPaymentMethod)}
                </span>
              </td>
              <td className="px-4 py-3 text-gray-400 text-xs">{s.claimedTransactionRef ?? '—'}</td>
              <td className="px-4 py-3 text-gray-500">{s.claimedPaidAt}</td>
              <td className="px-4 py-3">
                <a
                  href={resolveUploadUrl(s.receiptUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`text-xs font-medium hover:underline ${btnColor}`}
                >
                  {t('rent.viewReceipt', 'View Receipt')}
                </a>
              </td>
              <td className="px-4 py-3 text-right whitespace-nowrap">
                <button
                  onClick={() => approveMutation.mutate(s.id)}
                  disabled={approveMutation.isPending}
                  className="text-green-600 text-xs font-medium hover:underline mr-3 disabled:opacity-40"
                >
                  {t('rent.approve', 'Approve')}
                </button>
                <button
                  onClick={() => setRejectingId(s.id)}
                  className="text-red-600 text-xs font-medium hover:underline"
                >
                  {t('rent.reject', 'Reject')}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Pagination
        page={page}
        totalPages={Math.ceil(submissions.length / size) || 0}
        totalElements={submissions.length}
        size={size}
        onPageChange={setPage}
        className="px-4 pb-3"
      />

      {rejectingId && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-chrome-page rounded-xl p-6 w-80 shadow-xl">
            <h3 className="font-semibold mb-4">{t('rent.rejectSubmission', 'Reject Submission')}</h3>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('rent.rejectionReason', 'Reason')} *</label>
            <textarea
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              rows={3}
              className="w-full border rounded-lg px-3 py-2 text-sm"
              placeholder={t('rent.rejectionReasonPlaceholder', 'Why is this receipt being rejected?')}
            />
            {rejectMutation.isError && (
              <p className="text-red-500 text-xs mt-2">{(rejectMutation.error as Error).message}</p>
            )}
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => rejectMutation.mutate({ id: rejectingId, reason: rejectReason })}
                disabled={!rejectReason.trim() || rejectMutation.isPending}
                className="flex-1 bg-red-600 text-white rounded-lg py-2 text-sm font-medium disabled:opacity-40"
              >
                {rejectMutation.isPending ? t('owner.saving', 'Saving…') : t('rent.reject', 'Reject')}
              </button>
              <button
                onClick={() => { setRejectingId(null); setRejectReason(''); }}
                className="flex-1 border rounded-lg py-2 text-sm"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
